import React from 'react';

/**
 * Utility functions for formatting and parsing numbers with Vietnamese locale standards
 * Uses dots (.) as thousand separators and commas (,) as decimal separators
 * Displays whole numbers without unnecessary decimal places
 */

/**
 * Formats a number with Vietnamese formatting standards
 * @param value - The number to format
 * @param options - Formatting options
 * @returns Formatted string with Vietnamese number formatting
 */
export const formatNumberWithDots = (
  value: number | string,
  options?: {
    decimalPlaces?: number;
    thousandSeparator?: string;
    decimalSeparator?: string;
    hideDecimalsForWholeNumbers?: boolean;
  }
): string => {
  const decimalPlaces = options?.decimalPlaces ?? 0;
  const thousandSeparator = options?.thousandSeparator ?? '.';
  const decimalSeparator = options?.decimalSeparator ?? ',';
  const hideDecimalsForWholeNumbers = options?.hideDecimalsForWholeNumbers ?? true;

  if (value === '' || value === null || value === undefined) {
    return '';
  }

  const num = typeof value === 'string' ? Number(value) : value;

  if (Number.isNaN(num)) {
    return '';
  }

  const isNegative = num < 0;
  const absolute = Math.abs(num);

  // Check if the number is a whole number
  const isWholeNumber = absolute % 1 === 0;

  // If it's a whole number and we should hide decimals, don't show decimal places
  // If decimalPlaces is -1, preserve original decimal places (no limit)
  let actualDecimalPlaces = decimalPlaces;
  if (decimalPlaces === -1) {
    // No limit - preserve original decimal places
    actualDecimalPlaces = -1;
  } else if (isWholeNumber && hideDecimalsForWholeNumbers) {
    actualDecimalPlaces = 0;
  }

  // Convert to string and split to preserve original decimal representation
  const absoluteStr = String(absolute);
  const [intPartRaw, originalFracPart] = absoluteStr.split('.');

  // Insert thousand separators into integer part
  const intWithGrouping = intPartRaw.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);

  // Handle decimal part
  if (originalFracPart && (actualDecimalPlaces > 0 || actualDecimalPlaces === -1)) {
    // Limit decimal places if specified
    const fracPart = actualDecimalPlaces >= 0 && originalFracPart.length > actualDecimalPlaces
      ? originalFracPart.substring(0, actualDecimalPlaces)
      : originalFracPart;

    // Remove trailing zeros for cleaner display
    const cleanedFracPart = fracPart.replace(/0+$/, '');

    if (cleanedFracPart.length > 0) {
      const result = `${isNegative ? '-' : ''}${intWithGrouping}${decimalSeparator}${cleanedFracPart}`;
      return result;
    }
  }

  return `${isNegative ? '-' : ''}${intWithGrouping}`;
};

/**
 * Formats a quantity with smart decimal handling
 * Shows up to 3 decimal places but hides them for whole numbers
 */
export const formatQuantity = (value: number | string): string => {
  return formatNumberWithDots(value, {
    decimalPlaces: 3,
    hideDecimalsForWholeNumbers: true
  });
};

/**
 * Formats a price/currency amount with smart decimal handling
 * Shows up to 2 decimal places but hides them for whole numbers
 */
export const formatPrice = (value: number | string): string => {
  return formatNumberWithDots(value, {
    decimalPlaces: 2,
    hideDecimalsForWholeNumbers: true
  });
};

/**
 * Formats a general number with Vietnamese formatting
 * Whole numbers display without decimals
 */
export const formatNumber = (value: number | string): string => {
  return formatNumberWithDots(value, {
    decimalPlaces: 0,
    hideDecimalsForWholeNumbers: true
  });
};

/**
 * Formats currency with Vietnamese dong symbol
 * Uses smart decimal handling - whole numbers show without decimals
 */
export const formatCurrency = (value: number | string): string => {
  if (value === '' || value === null || value === undefined) {
    return '0 ₫';
  }

  const num = typeof value === 'string' ? Number(value) : value;

  if (Number.isNaN(num)) {
    return '0 ₫';
  }

  const formattedNumber = formatPrice(num);
  return `${formattedNumber} ₫`;
};

/**
 * Formats a number with thousand separators but preserves user's decimal input
 * @param value - The number to format
 * @param options - Formatting options
 * @returns Formatted string that preserves user's decimal input
 */
export const formatNumberPreserveDecimals = (
  value: number | string,
  options?: {
    maxDecimalPlaces?: number;
    thousandSeparator?: string;
    decimalSeparator?: string;
  }
): string => {
  const maxDecimalPlaces = options?.maxDecimalPlaces ?? 3;
  const thousandSeparator = options?.thousandSeparator ?? '.';
  const decimalSeparator = options?.decimalSeparator ?? ',';

  if (value === '' || value === null || value === undefined) {
    return '';
  }

  // Preserve string formatting to avoid losing trailing zeros
  const valueStr = String(value);
  const isNegative = valueStr.startsWith('-');
  const numPortion = isNegative ? valueStr.substring(1) : valueStr;
  
  // Validate that the numeric portion is valid
  if (Number.isNaN(parseFloat(numPortion.replace(',', '.')))) {
    return '';
  }

  // Use the original string to preserve decimal places as user typed
  let numStr = numPortion;
  
  // If it's a whole number, don't add decimal places
  if (numStr.indexOf('.') === -1 && numStr.indexOf(',') === -1) {
    // Just add thousand separators
    const intWithGrouping = numStr.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);
    return `${isNegative ? '-' : ''}${intWithGrouping}`;
  }

  // Normalize decimal separator to '.' for processing, then convert back
  const normalizedStr = numStr.replace(',', '.');
  const [intPart, decPart] = normalizedStr.split('.');
  
  // Limit decimal places to maxDecimalPlaces
  const limitedDecPart = decPart && decPart.length > maxDecimalPlaces 
    ? decPart.substring(0, maxDecimalPlaces) 
    : decPart;

  // Add thousand separators to integer part
  const intWithGrouping = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);

  // Use the original decimal separator
  const result = limitedDecPart 
    ? `${isNegative ? '-' : ''}${intWithGrouping}${decimalSeparator}${limitedDecPart}`
    : `${isNegative ? '-' : ''}${intWithGrouping}`;

  return result;
};


/**
 * Parses a formatted number string back to a number
 * Removes dots and converts to number
 * @param value - The formatted string to parse
 * @returns The parsed number
 */
export const parseFormattedNumber = (
  value: string,
  options?: {
    thousandSeparator?: string;
    decimalSeparator?: string;
    allowNegative?: boolean;
  }
): number => {
  if (!value || value.trim() === '') {
    return 0;
  }

  const thousandSeparator = options?.thousandSeparator ?? '.';
  const decimalSeparator = options?.decimalSeparator ?? ',';
  const allowNegative = options?.allowNegative ?? true;

  // Keep only digits, decimal separators (both '.' and ','), and optional leading '-'
  let cleaned = value.replace(/[^0-9.,-]/g, '');

  // If negatives not allowed, strip '-'
  if (!allowNegative) {
    cleaned = cleaned.replace(/-/g, '');
  } else {
    // Only allow a single leading '-'
    cleaned = cleaned.replace(/(?!^)-/g, '');
  }

  // Remove thousand separators
  if (thousandSeparator) {
    const tsRegex = new RegExp(`\\${thousandSeparator}`, 'g');
    cleaned = cleaned.replace(tsRegex, '');
  }

  // Normalize decimal separator to '.'
  if (decimalSeparator && decimalSeparator !== '.') {
    const dsRegex = new RegExp(`\\${decimalSeparator}`, 'g');
    cleaned = cleaned.replace(dsRegex, '.');
  }

  // If there are multiple dots, keep the first as decimal, remove others
  const firstDot = cleaned.indexOf('.');
  if (firstDot !== -1) {
    cleaned = cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '');
  }

  const parsed = parseFloat(cleaned);
  return Number.isNaN(parsed) ? 0 : parsed;
};

// Note: helper function and hook for formatted numbers were removed due to being unused.

/**
 * Props for the FormattedNumberInput component
 */
export interface FormattedNumberInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> {
  value: number;
  onChange: (value: number) => void;
  allowNegative?: boolean;
  decimalPlaces?: number;
  maxDecimalPlaces?: number;
  thousandSeparator?: string;
  decimalSeparator?: string;
  hideDecimalsForWholeNumbers?: boolean;
}

/**
 * A formatted number input component that handles Vietnamese number formatting
 * Supports smart decimal handling - whole numbers display without unnecessary decimals
 */
export const FormattedNumberInput: React.FC<FormattedNumberInputProps> = ({
  value,
  onChange,
  allowNegative = false,
  className = '',
  onBlur,
  decimalPlaces = -1, // -1 = không giới hạn số lẻ
  maxDecimalPlaces,
  thousandSeparator = '.',
  decimalSeparator = ',',
  hideDecimalsForWholeNumbers = true,
  ...props
}) => {
  const [isDecimalTyping, setIsDecimalTyping] = React.useState(false);

  // Create a reusable formatting function to avoid code duplication
  const formatValue = React.useCallback((val: number) => {
    if (maxDecimalPlaces !== undefined) {
      return formatNumberPreserveDecimals(val, { maxDecimalPlaces, thousandSeparator, decimalSeparator });
    }
    return formatNumberWithDots(val, { 
      decimalPlaces, 
      thousandSeparator, 
      decimalSeparator, 
      hideDecimalsForWholeNumbers 
    });
  }, [decimalPlaces, maxDecimalPlaces, thousandSeparator, decimalSeparator, hideDecimalsForWholeNumbers]);

  const [displayValue, setDisplayValue] = React.useState(() => formatValue(value));

  React.useEffect(() => {
    // Don't override user input when they are typing after decimal separator
    if (!isDecimalTyping) {
      setDisplayValue(formatValue(value));
    }
  }, [value, formatValue, isDecimalTyping]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;

    // Cho phép: số, dấu nghìn, dấu thập phân, và '-' (nếu allowNegative)
    const allowedPattern = allowNegative
      ? new RegExp(`[^0-9\\${thousandSeparator}\\${decimalSeparator}-]`, 'g')
      : new RegExp(`[^0-9\\${thousandSeparator}\\${decimalSeparator}]`, 'g');

    let cleanValue = raw.replace(allowedPattern, '');

    // Chuẩn hóa dấu '-': chỉ cho phép 1 dấu '-' đầu chuỗi khi allowNegative
    if (allowNegative) {
      const hasMinus = cleanValue.startsWith('-');
      cleanValue = cleanValue.replace(/-/g, '');
      if (hasMinus) cleanValue = `-${cleanValue}`;
    } else {
      cleanValue = cleanValue.replace(/-/g, '');
    }

    // Đảm bảo chỉ có 1 dấu thập phân
    const dsRegex = new RegExp(`\\${decimalSeparator}`, 'g');
    const matches = cleanValue.match(dsRegex);
    if (matches && matches.length > 1) {
      const first = cleanValue.indexOf(decimalSeparator);
      cleanValue =
        cleanValue.slice(0, first + 1) +
        cleanValue.slice(first + 1).replace(dsRegex, '');
    }

    const hasDecimal = cleanValue.includes(decimalSeparator);
    setIsDecimalTyping(hasDecimal);

    if (hasDecimal) {
      // === YÊU CẦU: Khi người dùng nhập dấu phẩy -> không format nữa ===
      // Giữ nguyên người dùng gõ (không chèn dấu nghìn, không đổi gì)
      setDisplayValue(cleanValue);

      const parsed = parseFormattedNumber(cleanValue, {
        thousandSeparator,
        decimalSeparator,
        allowNegative
      });
      onChange(allowNegative ? parsed : Math.max(0, parsed));
      return;
    }

    // Trường hợp CHƯA có dấu thập phân: cho phép format phần nguyên
    // Bỏ các dấu nghìn hiện có để re-group
    const tsRegex = new RegExp(`\\${thousandSeparator}`, 'g');
    const withoutTS = cleanValue.replace(tsRegex, '');

    let integerPart = withoutTS;
    if (integerPart && integerPart !== '-') {
      const isNeg = integerPart.startsWith('-');
      const abs = isNeg ? integerPart.slice(1) : integerPart;
      const grouped = abs.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);
      integerPart = isNeg ? `-${grouped}` : grouped;
    }

    setDisplayValue(integerPart);

    // parse về số cho onChange
    const parsed = parseFormattedNumber(cleanValue, {
      thousandSeparator,
      decimalSeparator,
      allowNegative
    });
    onChange(allowNegative ? parsed : Math.max(0, parsed));
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // If user was typing decimals, preserve their input, otherwise format normally
    if (!isDecimalTyping) {
      setDisplayValue(formatValue(value));
    }

    // Call original onBlur if provided
    if (onBlur) {
      onBlur(e);
    }
  };

  return (
    <input
      {...props}
      type="text"
      value={displayValue}
      onChange={handleInputChange}
      onBlur={handleInputBlur}
      className={className}
    />
  );
};

